import React, { useRef } from "react";
import { marked } from "marked";
import htmlToMarkDown, { sanitizeMarkdown } from "utils/htmlToMarkDownUtil";
import { getDefaultTierValues, removeVersionFromName, appendVersionToName, NewVersionPopup } from "../common";
import _, { find, isEmpty, isFunction, map, omit, split } from "lodash";
import Vocabulary from "components/Vocabulary";
import { nameRegexValidator } from "utils/formUtils";
import SubProductAPI from "restAPI/SubProductAPI";
import ModalUtil from "utils/modalUtil";
import message from "components/CustomMessage";
import { EditIcon, IntegrationIcon } from "components/Icons";
import { PermissionSetMap, SUBPRODUCT_ACTION_PERMISSION_MAP } from "constants/RBACConstants";
import ConfirmPopup from "components/ConfirmPopup";
import { CUSTOM_TIER } from "utils/tierUtil";
import ACLink from "ac-components/ACLink/ACLink";
import copy from "copy-to-clipboard";
import { hasPermission } from "utils/authService";
import { NOT_AVAILABLE } from "ac-components/ac-na-component/ac-na-component";
import CVSSModifierPopup from "components/cvss/CVSSModifierPopup";

export const OWNER_FIELDS = [
  "engineeringOwner",
  "securityOwner",
  "businessOwner",
  "complianceOwner",
  "supportOwner",
];

export const ValueToIdList = [...OWNER_FIELDS, "product", "teamDto"];

const getEnvironmentDataArr = (data) => _.map(["Production", "Staging"], (env) => {
  const envData = _.find(data, (envData) => envData.name === env) || {};
  return (({ 
      name: envData.name, 
      branch: envData.branch, 
      url: _.filter(_.split(envData.url, ",").map(_.trim), v => !_.isEmpty(v)), 
      id: envData.id 
  }));
});

export const updateDataBeforePush = ({
  data,
  confidentialityData,
  tierCategory,
  sourceProductId,
  sourceSubProductId,
}) => {
  // Append version name in name
  data.name = appendVersionToName(data);

  if(data.tier === CUSTOM_TIER){
    data.tier = null;
  }

  delete data.confidentiality_custom;
  ValueToIdList.forEach((v) => {
    if (data[v]) {
      data[v] = {
        id: data[v],
      };
    }
  });
  data.types = _.map(data.types, (id) => ({ id }));
  data.programmingLanguage = (data.programmingLanguage || "").toString();
  data.technologies = (data.technologies || "").toString();
  _.forEach(data.environments, (env) => (env.url = _.join(env.url, ",")));

  // convert html to markdown
  if (data.description) {
    data.description = htmlToMarkDown(data.description);
  }
  data.slaTemplateId = data.slaTemplateId || null;
  let keys = Object.keys(confidentialityData);
  if (data.tier && _.isEmpty(data.confidentialityOptions) && !keys.length) {
    const { selectedTierGlobalFields, categorizationConfidentiality } =
      getDefaultTierValues(data.tier, tierCategory);
    
    // In case of skip and save, the assessment settings for the selected tier isn't received by the form submit. Hence we add them manually
    for(const i in selectedTierGlobalFields){
      if(!_.has(data, i)){
        data[i] = selectedTierGlobalFields[i];
      }
    }
    keys = Object.keys(categorizationConfidentiality);
  }
  const length = keys.length;
  data.confidentiality =
    length === 3 ? "ALL" : length === 2 ? "TWO" : length === 1 ? "ONE" : "NONE";
  data.confidentialityOptions = keys.join(",");
  if (sourceProductId) {
    data.sourceProductId = sourceProductId;
  }
  if (sourceSubProductId) {
    data.sourceSubProductId = sourceSubProductId;
  }

  return data;
};

export const updateDataAfterGet = (data) => {
  ValueToIdList.forEach((v) => {
    if (data[v]) {
      data[v] = data[v].id;
    }
  });
  // Remove version name from name
  data.name = removeVersionFromName(data);

  if(!data.tier){
    data.tier = CUSTOM_TIER;
  } else {
    data.tier = !_.isNaN(+data.tier) ? +data.tier : data.tier;
  }

  // convert markdown to html
  if (data.description) {
    data.description = marked.parse(sanitizeMarkdown(data.description));
  }

  data.environments = getEnvironmentDataArr(data.environmentDtos);

  if (data.excludedToolList?.length > 0 || data.includedToolList?.length > 0) {
    const subProductToolsMap = {};

    _.forEach(data.includedToolList, (entry) => {
      subProductToolsMap[entry] = false;
    });
    _.forEach(data.excludedToolList, (entry) => {
      subProductToolsMap[entry] = true;
    });

    data["subProductToolsMap"] = subProductToolsMap;
  }
  data.types = _.map(data.types, "id");
  data.programmingLanguage = data.programmingLanguage
    ? data.programmingLanguage.split`,`
    : [];
  data.technologies = data.technologies ? data.technologies.split`,` : [];
  return data;
};

export const subproductNameFieldRule = (
  existingSubproduct,
  selectedProduct,
  validateAPIRef,
  form,
  isVersionsEnabled = false,
) => {
  return [
    {
      required: true,
      message: (
        <>
          Enter a <Vocabulary name="subProduct" isLowerCase={true} /> name.
        </>
      ),
    },
    {
      validator: (_, value) => {
        if (typeof value === "string" && value?.trim().length !== 0) {
          // const remains = value
          //   .split(NAME_REGEX_START_WITH_NUMBER)
          //   .filter(Boolean).length;
          const versionNumber = form.getFieldValue("versionNumber");
          value = appendVersionToName({ name: value, versionNumber });
          // if existingSubproduct is not present then validate the name
          if (
            (!existingSubproduct || (existingSubproduct && existingSubproduct.toLowerCase() !== value.toLowerCase())) &&
            selectedProduct &&
            value.trim().length <= (versionNumber ? 228 : 100)
          ) {
            // cancel the api
            if (validateAPIRef.current) {
              validateAPIRef.current();
            }

            const { promise, cancel } = SubProductAPI.validate({
              name: value,
              productId: selectedProduct,
            });

            validateAPIRef.current = cancel;

            const showVersionMessage = isVersionsEnabled && versionNumber;
            return promise.catch((res) =>
              Promise.reject(`Name ${showVersionMessage ? '& version combination ' : ''}already exists.`)
            );
          }
        }
        return Promise.resolve();
      },
    },
    nameRegexValidator({ regex: "", max: 100 }),
  ];
};

export const openSubProductArchivePopup = (activeSubProductId, history, refresh) => {
  ModalUtil.show({
    content: (
      <ConfirmPopup
        promptMessage={
          <>
            This <Vocabulary name={"subProduct"} /> will be moved into the archive
            Organization, and all security tool configurations will be disabled
          </>
        }
        okText="Archive"
        note
        onOk={async (note) => {
          await SubProductAPI.archiveSubProduct(activeSubProductId, note).promise.then(() => {
            message.success(
              <>
                <Vocabulary name={"subProduct"} /> successfully archived.
              </>
            );
            if(isFunction(refresh)){
              refresh();
            }
            else {
              history.push("/subproduct");
            }
          });
        }}
      />
    ),
  });
};

export const openSubProductNewVersionPopup = ({ subproduct, afterClose }) => {
  const defaultStatus = subproduct.status || "";
  const initialValues = {
    existingVersion: subproduct.versionNumber,
    cloneManualFindings: false,
    status: defaultStatus
  };
  const onSubmit = async (data) => {
    const response = await SubProductAPI.createVersion({ subProductId: +subproduct.id, version: data.version, status: data.status, cloneManualFindings: data.cloneManualFindings }).promise;
    return response;
  };
  ModalUtil.show({
    content: (
      <NewVersionPopup initialValues={initialValues} onSubmit={onSubmit} />
    ),
    afterClose: (newSubProduct) => {
      if (newSubProduct) {
        message.success('Request is accepted. Please check after sometime.');
      }
      afterClose(newSubProduct);
    }
  });
};

export const actionItems = [{
  type: "integration",
  text: "Integration",
  icon: <IntegrationIcon style={{color: "#429FF4"}} />,
  permission: PermissionSetMap.EDIT_TOOL_CONFIG
}, {
  type: "edit",
  text: "Edit",
  icon: <EditIcon />,
  permission: PermissionSetMap.UPDATE_SUB_PRODUCT
}]

export const programmingLanguages = ["Go", "C", "C++", "Java", "ErLang", "Python", "PHP", "JavaScript", ".net", "C#"];
export const ORIGIN_OPTIONS = ["Third party library", "Purchased", "Contractor developed", "Internally developed", "Open source", "Outsourced"]
export const formatProgLang = (proStr) => proStr ? proStr.split`,`.join`, ` : null;

export const SubProductModalCommonConfig = {
  width: 778,
  mask: true,
  settingsPrefixKey: "subproduct-details",
  defaultPlacement: "right",
  resize: true,
};

export const RenderEnvironmentURL = ({environmentDtos, name}) => {
  const URL = find(environmentDtos, { name })?.url ?? "";
  const messageRef = useRef();

  const copyTextToClipBoard = (url) => {
    if (messageRef.current) {
      messageRef.current();
    }
    copy(url);
    messageRef.current = message.success("Copied to clipboard");
  };

  if (isEmpty(URL)) {
    return <div className="text-small-default color-default text-ellipsis">{NOT_AVAILABLE}</div>;
  }
  return (
    <>
      {map(split(URL, ","), (url) => (
        <div title={url} className="text-ellipsis"> 
          <ACLink
            className="m-r-xs"
            onClick={() => copyTextToClipBoard(url)}
            overlay
            primary={false}
          >
            {url}
          </ACLink>
        </div>
      ))}
    </>
  );
};

export const updateBasicSubProductDataAfterGet = (data) => {
  // Remove version name from name
  data.name = removeVersionFromName(data);

  if(!data.tier){
    data.tier = CUSTOM_TIER;
  } else {
    data.tier = !_.isNaN(+data.tier) ? +data.tier : data.tier;
  }

  // convert markdown to html
  if (data.description) {
    data.description = marked.parse(sanitizeMarkdown(data.description));
  }

  data.environments = getEnvironmentDataArr(data.environmentDtos);

  if(data.environmentDtos){
    const productionURL = find(data.environmentDtos, { name: "Production" })?.url;
    const stagingURL = find(data.environmentDtos, { name: "Staging" })?.url;
    data.productionURL = !isEmpty(productionURL) ? split(productionURL, ",") : undefined
    data.stagingURL =  !isEmpty(stagingURL) ? split(stagingURL, ",") : undefined;
  }

  data.types = _.map(data.types, "id");
  data.programmingLanguage = data.programmingLanguage
    ? data.programmingLanguage.split`,`
    : [];
  data.technologies = data.technologies ? data.technologies.split`,` : [];
  return data;
};

export const REPOSITORY_TYPE_OPTIONS = [
  { label: "GitHub", value: "GITHUB" },
  { label: "GitLab", value: "GITLAB" },
  { label: "SVN", value: "SVN" },
  { label: "Azure Repos", value: "AZURE_REPOS" },
  { label: "BitBucket", value: "BITBUCKET" },
  { label: "AWS CodeCommit", value: "AWS_CODE_COMMIT" },
  { label: "Other", value: "other" },
];

export const updateBasicSubProductDataBeforePush = (data, {environmentDtos}) => {
  // Append version name in name
  data.name = appendVersionToName(data);

  if (data.tier === CUSTOM_TIER) {
    data.tier = null;
  }

  // Convert description from HTML to Markdown (if present)
  if (data.description) {
    data.description = htmlToMarkDown(data.description);
  }

  // Convert `types` to [{ id: type }]
  if (data.types) {
    data.types = map(data.types, (id) => ({ id }));
  }

  if (data.programmingLanguage) {
    data.programmingLanguage = (data.programmingLanguage || "").toString();
  }

  if (data.technologies) {
    data.technologies = (data.technologies || "").toString();
  }

  if(data.productionURL || data.stagingURL) {
    const environments = getEnvironmentDataArr(environmentDtos);
    data.environments = map(environments,(env) => ({...env, url: env.name === "Production" && data.productionURL ? data.productionURL.join(",") : data.stagingURL.join(",")}));
  }
  omit(data, ["productionURL", "stagingURL"])

  return data;
};

export const getDisabledActionAndTooltipForSubProduct = (action) => {
  const noPermission = !hasPermission(
    SUBPRODUCT_ACTION_PERMISSION_MAP[action],
  );
  return {
    tooltip: noPermission ? "No Access" : "",
    disabled: noPermission,
  };
};

export const openCVSSModifierModal = (subProductId, modalRenderer, initialCVSSVector, refresh, inheritedCVSSModifier) => {
  const onSubmit = async (cvssVector) => {
    await SubProductAPI.updateOtherProperties(subProductId, {
      properties: {
        modifiedVector: cvssVector,
      },
    }).promise;
  };
  modalRenderer.show({
    content: (
      <CVSSModifierPopup
        onSubmit={onSubmit}
        titlePrefix={<Vocabulary name="subProduct" />}
        subtitle={<><Vocabulary name="subProduct" />-level modifiers override <Vocabulary name="product" /> defaults and become the defaults for the finding when Not Defined. Any level can override.</>}
        showHeader={false}
        initialCvSSString={initialCVSSVector}
        inheritedCVSSModifier={inheritedCVSSModifier}
      />
    ),
    width: 1056,
    onClose: refresh
  });
};
